import debug from "debug";

const log = debug("app:inactivity");

type UserState = "guest" | "authenticated" | "expired";

interface ExperienceLike {
  eventHub: any;
  userState?: UserState;
  LoginManager?: { handleSessionExpiration?: () => void };
}

/**
 * Automatically logs the user out after a period of inactivity, similar to banking apps.
 * - After `idleSeconds` of no user input, shows a warning modal.
 * - If there is still no activity after `warningSeconds`, triggers session expiration/logout.
 *
 * Activity is defined as any mouse move, mouse down, key down, scroll, or touch start.
 */
class InactivityLogoutManager {
  private experience: ExperienceLike;
  private eventHub: any;
  private idleTimeoutMs: number;
  private warningTimeoutMs: number;
  private idleTimer: any = null;
  private warningTimer: any = null;
  private trackingEnabled = false;

  /**
   * @param experience - The main Experience instance (or compatible object).
   * @param idleSeconds - Seconds of inactivity before showing the warning modal.
   * @param warningSeconds - Seconds after the warning before automatic sign-out.
   */
  constructor(
    experience: ExperienceLike,
    idleSeconds = 300,
    warningSeconds = 300,
  ) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.idleTimeoutMs = idleSeconds * 1000;
    this.warningTimeoutMs = warningSeconds * 1000;

    this.setupAuthListeners();
    this.setupDomActivityListeners();
    this.syncWithUserState();
    log(
      `InactivityLogoutManager initialized (idle=${idleSeconds}s, warning=${warningSeconds}s)`,
    );
  }

  /**
   * Enable/disable tracking based on authentication events.
   */
  private setupAuthListeners() {
    this.eventHub.on("auth:login", () => {
      log("auth:login → enable inactivity tracking");
      this.enableTracking();
    });

    this.eventHub.on("auth:guest", () => {
      log("auth:guest → disable inactivity tracking");
      this.disableTracking();
    });

    this.eventHub.on("sign_out", () => {
      log("sign_out → disable inactivity tracking");
      this.disableTracking();
    });

    this.eventHub.on("sessionExpired", () => {
      log("sessionExpired → disable inactivity tracking");
      this.disableTracking();
    });
  }

  /**
   * Low-level DOM listeners for user input.
   */
  private setupDomActivityListeners() {
    const handler = this.handleActivity.bind(this);
    window.addEventListener("mousemove", handler, true);
    window.addEventListener("mousedown", handler, true);
    window.addEventListener("keydown", handler, true);
    window.addEventListener("scroll", handler, true);
    window.addEventListener("touchstart", handler, true);
  }

  /**
   * Initialize tracking state from the centralized userState if available.
   */
  private syncWithUserState() {
    const state = this.experience.userState;
    if (state === "authenticated") {
      this.enableTracking();
    } else {
      this.disableTracking();
    }
  }

  private enableTracking() {
    if (this.trackingEnabled) return;
    this.trackingEnabled = true;
    log("Inactivity tracking enabled");
    this.resetIdleTimer();
  }

  private disableTracking() {
    if (!this.trackingEnabled) {
      this.clearTimers();
      return;
    }
    this.trackingEnabled = false;
    log("Inactivity tracking disabled");
    this.clearTimers();
  }

  private clearTimers() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Called on any user input. Resets idle countdown if tracking is enabled.
   */
  private handleActivity() {
    if (!this.trackingEnabled) return;
    this.resetIdleTimer();
  }

  /**
   * Restart the idle countdown and clear any pending warning/logout timers.
   */
  private resetIdleTimer() {
    this.clearTimers();
    this.idleTimer = setTimeout(() => this.onIdleTimeout(), this.idleTimeoutMs);
  }

  /**
   * Called after idleTimeoutMs of no input.
   * Shows a warning modal and schedules automatic sign-out.
   */
  private onIdleTimeout() {
    log("User idle threshold reached, showing inactivity warning");

    // Close any existing modals so the inactivity warning is clearly visible.
    // Use the same pattern as other modal scripts: dispatch a global "closeModal" event
    // so individual modal managers can clean themselves up.
    try {
      document.dispatchEvent(new CustomEvent("closeModal"));

      // Additionally, hide the global modal wrapper if it's currently visible
      const globalModalWrapper = document.querySelector(
        ".global-modal-wrapper",
      ) as HTMLElement | null;
      if (globalModalWrapper) {
        globalModalWrapper.classList.remove("visible");
      }

      // And hide common toolbar/background modals that use these IDs if present
      const modalIds = [
        "loadModal",
        "save-modal",
        "new-modal",
        "rename-modal",
        "backgroundModal",
      ];
      modalIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          el.style.display = "none";
        }
      });
    } catch (error) {
      log(
        "Error while closing existing modals before inactivity warning:",
        error,
      );
    }

    // Show warning popup via global modal system
    this.eventHub.emit("modalInfo", {
      title: "Are you still there?",
      message:
        "No activity has been detected for 15 minutes. " +
        "If there is no further activity in the next 15 minutes, you will be signed out automatically.",
      actions: [
        {
          label: "Stay signed in",
          callback: () => {
            log("User chose to stay signed in from inactivity modal");
            this.resetIdleTimer();
          },
        },
        {
          label: "Sign out now",
          callback: () => {
            log("User chose to sign out immediately from inactivity modal");
            this.forceSignOut();
          },
        },
      ],
      autoClose: false,
    });

    // Schedule automatic sign-out in the warning window
    this.warningTimer = setTimeout(() => {
      log("Inactivity warning window elapsed, forcing sign out");
      this.forceSignOut();
    }, this.warningTimeoutMs);
  }

  /**
   * Trigger the existing centralized sign-out/session-expired flow.
   */
  private forceSignOut() {
    this.disableTracking();

    if (this.eventHub) {
      // Let LoginManager.handleSessionExpiration handle UI + logout flow
      this.eventHub.emit("sessionExpired");
    } else if (this.experience.LoginManager?.handleSessionExpiration) {
      this.experience.LoginManager.handleSessionExpiration();
    }
  }

  cleanup() {
    log("Cleaning up InactivityLogoutManager");
    this.disableTracking();
    // Note: DOM listeners are intentionally left; app lifetime == window lifetime.
  }
}

export default InactivityLogoutManager;
